/**
 * Period domain logic — FR-5 (SIH-6 scope, slice-1 subset mounted now per the
 * design README J2: the period selector is persistent across screens and the
 * default landing state is period = Today, BR-3).
 *
 * This module holds period INPUT validation only. It derives NO sync status
 * (ADR-000 F2: derivation lives only in the API tier).
 */

import type { PeriodSelection } from './types'

/** yyyy-MM-dd for a Date in the browser's local timezone. */
export function toDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Add n days to a yyyy-MM-dd key (UTC-safe day arithmetic on the parsed date). */
export function addDays(key: string, n: number): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number]
  const date = new Date(y, (m ?? 1) - 1, d ?? 1)
  date.setDate(date.getDate() + n)
  return toDateKey(date)
}

/**
 * FR-5 validation: custom range enforces from ≤ to (boundary-inclusive both
 * ends). Reversed or malformed ranges are rejected inline before any query.
 */
export function validateCustomRange(from: string, to: string): { ok: true } | { ok: false; reason: string } {
  const dateRe = /^\d{4}-\d{2}-\d{2}$/
  if (!dateRe.test(from)) return { ok: false, reason: 'From date must be a valid date (yyyy-MM-dd).' }
  if (!dateRe.test(to)) return { ok: false, reason: 'To date must be a valid date (yyyy-MM-dd).' }
  if (from > to) return { ok: false, reason: 'From must be on or before To (from ≤ to).' }
  return { ok: true }
}

/**
 * Resolve the concrete inclusive from/to for a period selection. Custom
 * ranges pass through validation; presets resolve relative to today.
 */
export function resolvePeriod(period: PeriodSelection): { from: string; to: string } {
  switch (period.preset) {
    case 'TODAY': {
      const today = toDateKey(new Date())
      return { from: today, to: today }
    }
    case 'LAST_7_DAYS': {
      const to = toDateKey(new Date())
      return { from: addDays(to, -6), to }
    }
    case 'LAST_30_DAYS': {
      const to = toDateKey(new Date())
      return { from: addDays(to, -29), to }
    }
    case 'CUSTOM': {
      const from = period.from ?? ''
      const to = period.to ?? ''
      const verdict = validateCustomRange(from, to)
      if (!verdict.ok) throw new Error(verdict.reason)
      return { from, to }
    }
  }
}
